import { useEffect, useRef, useState } from 'react';
import { endCall, type ActiveCallContext } from '@/domain/callFlow';
import { useSimulationStore } from '@/state/simulationStore';
import { PhoneOff, Mic, Wallet, Sparkles } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  call: ActiveCallContext;
}

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export default function LiveCallBanner({ call }: Props) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const [tick, setTick] = useState(0);
  const transcriptRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    transcriptRef.current?.scrollTo({
      top: transcriptRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [call.transcript.length]);

  const elapsedSec = Math.max(
    0,
    Math.round(
      (new Date(clockIso).getTime() - new Date(call.startedAt).getTime()) / 1000,
    ) + tick,
  );

  return (
    <section
      data-testid="live-call-banner"
      className="rounded-lg border border-gold-bright/50 bg-gradient-to-r from-gold/10 via-ink-deep to-ink-deep px-5 py-4 mb-4 shadow-gold-glow"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-mint animate-ping opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-mint" />
          </span>
          <div>
            <p className="label-small-caps text-gold-bright">
              Live call · {call.direction}
            </p>
            <p className="font-inter text-[11px] text-cream-soft mt-0.5 uppercase tracking-widest">
              {call.status} · {formatDuration(elapsedSec)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-ghost text-[11px]"
            data-testid="live-call-deposit"
            title="Send Stripe deposit link"
          >
            <Wallet size={12} /> Deposit link
          </button>
          <button
            type="button"
            className="btn-ghost text-[11px]"
            data-testid="live-call-future"
            title="Tag future-readiness"
          >
            <Sparkles size={12} /> Future-readiness
          </button>
          <button
            type="button"
            className="btn-ghost text-[11px]"
            data-testid="live-call-mute"
            title="Mute"
          >
            <Mic size={12} /> Mute
          </button>
          <button
            type="button"
            onClick={() => endCall({ tags: [], notes: '' })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-blood text-blood hover:bg-blood/10 transition"
            data-testid="live-call-hangup"
          >
            <PhoneOff size={12} /> Hang up
          </button>
        </div>
      </div>

      <ol
        ref={transcriptRef}
        data-testid="live-call-transcript"
        className="mt-4 max-h-56 overflow-y-auto pr-2 space-y-1.5 border-t border-gold-dim/20 pt-3"
      >
        {call.transcript.length === 0 ? (
          <li className="font-serif text-cream-soft text-sm">Connecting…</li>
        ) : null}
        {call.transcript.map((line) => (
          <li key={line.id} className="flex gap-3 text-sm">
            <span
              className={clsx(
                'shrink-0 font-inter text-[10px] uppercase tracking-widest pt-0.5',
                line.speaker === 'customer'
                  ? 'text-gold-bright'
                  : line.speaker === 'ai-voice'
                    ? 'text-mint'
                    : 'text-cream-soft',
              )}
            >
              {line.speaker}
            </span>
            <span className="font-serif text-cream leading-relaxed">{line.body}</span>
          </li>
        ))}
        {call.status === 'live' ? (
          <li
            className="flex gap-3 text-sm pt-1"
            data-testid="live-call-typing"
            aria-label="Listening"
          >
            <span className="shrink-0 font-inter text-[10px] uppercase tracking-widest pt-0.5 text-cream-soft">
              ···
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-cream-soft/70 animate-pulse" />
              <span
                className="inline-block h-1.5 w-1.5 rounded-full bg-cream-soft/70 animate-pulse"
                style={{ animationDelay: '150ms' }}
              />
              <span
                className="inline-block h-1.5 w-1.5 rounded-full bg-cream-soft/70 animate-pulse"
                style={{ animationDelay: '300ms' }}
              />
            </span>
          </li>
        ) : null}
      </ol>
    </section>
  );
}
